/**
 * Property metadata helpers for Model.properties rich shape (AC-1251).
 *
 * A property value stored on Model.properties may take one of two shapes:
 *
 *   - Shorthand (legacy):  "int"
 *   - Rich object (new):   { type: "int", description: "...", min: 0, max: 100 }
 *
 * This module mirrors backend/schemas/property_meta.py and provides:
 *   - PropertyMeta         canonical normalized form
 *   - PropertyDefinition   union accepted on input (string | rich object)
 *   - normalizeProperty    normalize either shape into PropertyMeta
 *   - serializeProperty    emit shorthand when no metadata, rich object otherwise
 *   - isNumericType        drive Min/Max visibility in the Model editor
 */

import type { PropertyMeta, PropertyDefinition } from '../types';

export type { PropertyMeta, PropertyDefinition };

/**
 * Accept either the legacy shorthand string or the rich object; return a
 * canonical PropertyMeta. Falls back to `{ type: 'unknown' }` for malformed
 * input so callers can safely render hints without runtime errors.
 */
export function normalizeProperty(
  value: PropertyDefinition | unknown,
): PropertyMeta {
  if (typeof value === 'string') {
    return { type: value };
  }
  if (value && typeof value === 'object' && 'type' in (value as any)) {
    const v = value as Record<string, unknown>;
    return {
      type: String(v.type),
      description:
        typeof v.description === 'string' && v.description.length > 0
          ? v.description
          : undefined,
      min: typeof v.min === 'number' ? v.min : undefined,
      max: typeof v.max === 'number' ? v.max : undefined,
    };
  }
  return { type: 'unknown' };
}

/**
 * Emit the compact shorthand string when no metadata is attached; otherwise
 * return a rich object. Keeps payloads minimal for users who do not opt in.
 */
export function serializeProperty(meta: PropertyMeta): PropertyDefinition {
  const hasMeta =
    (typeof meta.description === 'string' && meta.description.length > 0) ||
    typeof meta.min === 'number' ||
    typeof meta.max === 'number';
  if (!hasMeta) {
    return meta.type;
  }
  const rich: {
    type: string;
    description?: string;
    min?: number;
    max?: number;
  } = { type: meta.type };
  if (typeof meta.description === 'string' && meta.description.length > 0) {
    rich.description = meta.description;
  }
  if (typeof meta.min === 'number') {
    rich.min = meta.min;
  }
  if (typeof meta.max === 'number') {
    rich.max = meta.max;
  }
  return rich;
}

/** Is the normalized type numeric? Controls Min/Max visibility. */
export function isNumericType(type: string): boolean {
  const t = (type || '').toLowerCase();
  return t === 'int' || t === 'float' || t === 'number';
}
